import React, { useState, useRef, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSupplierPortalData } from "../../redux/actions/getSupplierPortalDataSlice";
import { updateUserResponse } from "../../redux/actions/updateUserResponseSlice";
import { getSupplierPortalDataEndpoint, updateUserResponseEndpoint } from "../../common/endpoints";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
    Box,
    Typography,
    Paper,
    Chip,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
    Badge,
    Popover,
    Select,
    Checkbox,
    ListItemText,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import TopSection from "../LT/TopSection";
import CustomButton from "../../components/common/button/Button";
import { toast } from 'react-toastify';
import Cards from "../../components/common/card/Card";

const STATUS_META = {
    "NOT SENT": { label: "Awaiting Response", textColor: "#92400E", bgColor: "#FEF3C7" },
    APPROVED: { label: "Approved", textColor: "#14532D", bgColor: "#DCFCE7" },
    REJECTED: { label: "Rejected", textColor: "#991B1B", bgColor: "#FEE2E2" },
    "COUNTER OFFER": { label: "Counter Offer", textColor: "#581C87", bgColor: "#F3E8FF" },
    PENDING: { label: "Awaiting Response", textColor: "#92400E", bgColor: "#FEF3C7" },
    DEFAULT: { label: "Awaiting Response", textColor: "#92400E", bgColor: "#FEF3C7" },
};

const fieldRow = (label, oldVal, newVal, changed) => ({
    label,
    oldVal,
    newVal,
    changed: changed ?? oldVal !== newVal,
});

const TAB_FILTER_MAP = {
    all: "allRequest",
    pending: "pendingResponse",
    responded: "responded",
};

const POExceptionMgmtSupplier = () => {
    const gridRef = useRef(null);
    const dispatch = useDispatch();
    const supplierPortalDataSelector = useSelector((state) => state.getSupplierPortalData);
    const [requests, setRequests] = useState([]);
    const [activeTab, setActiveTab] = useState("pending");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedReq, setSelectedReq] = useState(null);
    const [responseType, setResponseType] = useState("APPROVED");
    const [responseNote, setResponseNote] = useState("");
    const [counterDate, setCounterDate] = useState("");
    const [counterHandoverDate, setCounterHandoverDate] = useState("");
    const [counterQty, setCounterQty] = useState("");
    const [counterShippingInstruction, setCounterShippingInstruction] = useState("");
    const [detailOpen, setDetailOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [disableFilterBtn, setDisableFilterBtn] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [detailReq, setDetailReq] = useState(null);
    const [tabCounts, setTabCounts] = useState({ all: null, pending: null, responded: null });

    const GRID_FILTER_MODEL_STORAGE_KEY = 'poExceptionManagement.gridFilterModel';
    useEffect(() => {
        dispatch(
            getSupplierPortalData({
                dynamicEndpoint: getSupplierPortalDataEndpoint,
                data: { filterCondition: TAB_FILTER_MAP[activeTab] },
            })
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (supplierPortalDataSelector?.data) {
            setRequests(supplierPortalDataSelector.data);
            setTabCounts((prev) => ({ ...prev, [activeTab]: supplierPortalDataSelector.data.length }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplierPortalDataSelector?.data]);

    const filtered = requests;

    const openResponseDialog = (req) => {
        setSelectedReq(req);
        setResponseType("APPROVED");
        setResponseNote(req.responseNote || "");
        setCounterDate(req.counterDate || "");
        setCounterHandoverDate(req.counterHandoverDate || "");
        setCounterQty(req.counterQty || "");
        setCounterShippingInstruction(req.counterShippingInstruction || "");
        setDialogOpen(true);
    };

    // eslint-disable-next-line no-unused-vars
    const openDetailDialog = (req) => {
    };

    const handleSubmitResponse = async () => {
        const requestBody = {
            plantId: selectedReq.PLANT_ID || '',
            supplierId: selectedReq.VENDOR_ID || '',
            poNo: selectedReq.PO_NO || '',
            poItem: selectedReq.ITEM || '',
            userResponse: responseType === 'COUNTER' ? 'Counter Offer' : responseType === 'APPROVED' ? 'Approved' : 'Rejected',
            quantityEdited: counterQty !== '' ? Number(counterQty) : null,
            supplierConfirmedDeliveryDate: counterDate || null,
            supplierConfirmedHandoverDate: counterHandoverDate || null,
            shippingInstruction: counterShippingInstruction || null,
            userComments: responseNote || null,
            mgmtId: selectedReq.MGMT_ID || null,
        };
        try {
            const response = await dispatch(updateUserResponse({
                dynamicEndpoint: updateUserResponseEndpoint,
                data: requestBody,
            }));
            if (response?.meta?.requestStatus === 'fulfilled') {
                setDialogOpen(false);
                toast.success('Updated successfully.', { autoClose: 2000 });
                dispatch(getSupplierPortalData({
                    dynamicEndpoint: getSupplierPortalDataEndpoint,
                    data: { filterCondition: TAB_FILTER_MAP[activeTab] },
                }));
            } else {
                console.error('Failed to submit response');
            }
        } catch (e) {
            console.error('Failed to submit response', e);
        }
    };

    const chipFor = (status) => {
        const meta = STATUS_META[(status || '').toUpperCase()] || STATUS_META.DEFAULT;
        return (
            <Chip
                label={meta.label}
                size="small"
                sx={{
                    backgroundColor: meta.bgColor,
                    color: meta.textColor,
                    fontWeight: 600,
                    fontSize: "11px",
                    height: 22,
                    borderRadius: "10px",
                }}
            />
        );
    };

    const onFilterChanged = () => {
        if (gridRef.current) {
            const filterModel = gridRef.current.getFilterModel();
            try {
                sessionStorage.setItem(GRID_FILTER_MODEL_STORAGE_KEY, JSON.stringify(filterModel || {}));
            } catch (e) {
                // ignore storage errors
            }
            setDisableFilterBtn(Object.keys(filterModel).length === 0);
        }
    };

    const onGridReady = (params) => {
        gridRef.current = params?.api;
    };


    const gridOptions = {
        getRowId: (params) => {
            const d = params?.data || {};
            return d.MGMT_ID;
        },
        onFilterChanged,
        overlayLoadingTemplate: `<span class="ag--overlay-loading-center" >Please wait while data is loading</span>`,
        overlayNoRowsTemplate: `<span class="ag--overlay-no-rows-center" >No Data Available</span>`,
        defaultColDef: {
            sortable: true,
            filter: 'agMultiColumnFilter',
            filterParams: {
                filters: [
                    {
                        filter: 'agTextColumnFilter',
                        filterParams: {
                            suppressMiniFilter: false,
                        },
                    },
                    {
                        filter: 'agSetColumnFilter',
                        filterParams: {
                            excelMode: 'windows',
                            suppressMiniFilter: false,
                            buttons: ['apply', 'reset'],
                        },
                    },
                ],
            },
            cellStyle: {
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.5,
            display: "flex",
            alignItems: "center",
        },
        valueGetter: (params) => {
            if (!params.data) return "-";

            const field = params.colDef.field;
            const value = params.data[field];

            return value === null || value === undefined || value === "" ? "-" : value;
        },
        }

  }

    const columnDefs = useMemo(() => [
        {
            field: "PO_NO",
            headerName: "PO",
            width: 150,
            cellStyle: { fontWeight: 600, color: "#111827" },
        },
        {
            field: "PO_ITEM_NO",
            headerName: "PO Item No",
            width: 120,
        },
        {
            field: "MATERIAL_ID",
            headerName: "Material Number",
            width: 140,
        },
        // {
        //     field: "MATERIAL_NAME",
        //     headerName: "Material Description",
        //     width: 220,
        // },
        {
            field: "PLANT_ID",
            headerName: "Plant Code",
            width: 110,
        },
        {
            field: "PO_QUANTITY",
            headerName: "PO Qty",
            width: 120,
        },
        {
            field: "NEW_PO_QUANTITY",
            headerName: "PO Qty (New)",
            width: 120,
        },
        {
            field: "UOM",
            headerName: "UOM",
            width: 90,
        },
        {
            field: "HANDOVER_DATE",
            headerName: "Handover Date",
            width: 190,
        },
        {
            field: "NEW_HANDOVER_DATE",
            headerName: "Handover Date (New)",
            width: 190,
            cellStyle: { fontWeight: 600, color: "#C2410C" },
            valueFormatter: (params) => params.value || "—",
        },
        {
            field: "DELIVERY_DATE",
            headerName: "Delivery Date",
            width: 190,
        },
        {
            field: "NEW_DELIVERY_DATE",
            headerName: "Delivery Date (New)",
            width: 190,
            cellStyle: { fontWeight: 600, color: "#C2410C" },
            valueFormatter: (params) => params.value || "—",
        },
        {
            field: "SUPPLIER_CONFIRMED_HANDOVER_DATE",
            headerName: "Supplier Confirmed Handover Date",
            width: 210,
            valueFormatter: (params) => params.value || "—",
        },
        {
            field: "SUPPLIER_CONFIRMED_DELIVERY_DATE",
            headerName: "Supplier Confirmed Delivery Date",
            width: 210,
            valueFormatter: (params) => params.value || "—",
        },
        {
            field: 'PO_SHIPPING_INSTRUCTION',
            headerName: 'PO Shipping Instructions',
            width: 220,
        },
        {
            field: 'NEW_PO_SHIPPING_INSTRUCTION',
            headerName: 'PO Shipping Instructions (New)',
            width: 220,
        },
        {
            field: "RECORD_STATUS",
            headerName: "Status",
            width: 160,
            cellRenderer: (params) => chipFor(params.value),
        },
        {
            field: "COLLABORATION_STATUS",
            headerName: "Collaboration Status",
            width: 190,
            cellRenderer: (params) => {
                const val = (params.value || '').trim();
                let label, textColor, bgColor;
                if (val === 'Accept') {
                    label = 'Acknowledged';
                    textColor = '#15803d';
                    bgColor = '#DCFCE7';
                } else if (val === 'Defer') {
                    label = 'Collaboration Required';
                    textColor = '#b45309';
                    bgColor = '#FEF3C7';
                } else {
                    label = 'Ongoing';
                    textColor = '#1D4ED8';
                    bgColor = '#DBEAFE';
                }
                return (
                    <Typography
                        sx={{
                            fontWeight: 500,
                            fontSize: '12px',
                            px: 1,
                            py: 0.25,
                            borderRadius: '10px',
                            display: 'inline-block',
                            color: textColor,
                            backgroundColor: bgColor,
                        }}
                    >
                        {label}
                    </Typography>
                );
            },
        },
        {
            field: "MGMT_ID",
            headerName: "Actions",
            width: 120,
            sortable: false,
            filter: false,
            cellRenderer: (params) => (
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditOutlinedIcon sx={{ fontSize: 13 }} />}
                    onClick={() => openResponseDialog(params.data)}
                    sx={{
                        textTransform: "none",
                        fontSize: "11px",
                        px: 1.5,
                        py: 0.4,
                        borderRadius: "6px",
                        borderColor: "#D1D5DB",
                        color: "#374151",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: "#9CA3AF", backgroundColor: "#F9FAFB" },
                    }}
                >
                    Update
                </Button>
            ),
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [requests]);
    // eslint-disable-next-line no-unused-vars
    const [activeItem, setActiveItem] = useState(localStorage.getItem("poExceptionSupplierActiveItem") || "Total Requests");
    const cardData = [
        { title: "Total Requests",value:"2" },
        { title: "Awaiting Response" },
        { title: "Responded" },
        { title: "Counter Offers" },
    ];

    // const callBackFunction = (index, title, url) => {
    //     setActiveItem(title);
    //     localStorage.setItem("poExceptionBuyerActiveItem", title)
    // }

    const handleClickHide = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseHide = () => {
        setAnchorEl(null);
    };

    const handleColumnChange = (event) => {
        setSelectedColumns(event.target.value || []);
    };

    const columnFilterDropdownOption = useMemo(() => (
        columnDefs
            .filter((c) => c.field || c.headerName)
            .map((c) => ({
                value: c.field || c.headerName,
                label: c.headerName || c.field,
            }))
    ), []);

    const renderDropdownValue = (selected) => {
        if (!selected || selected.length === 0) return <em>Select Columns</em>;
        return selected
            .map((field) => columnFilterDropdownOption.find((opt) => opt.value === field)?.label || field)
            .join(', ');
    };

    const handleSave = () => {
        setAnchorEl(null);
        toast.success('Columns updated', { autoClose: 1000 });
    };

    const clearAllFilters = () => {
        if (gridRef.current) {
            gridRef.current.setFilterModel(null);
            setDisableFilterBtn(true);
            try {
                sessionStorage.removeItem(GRID_FILTER_MODEL_STORAGE_KEY);
            } catch (e) {
                // ignore storage errors
            }
        }
    };
    return (
        <div>
            <TopSection
                labelList={[
                    { title: "Home", path: "/dashboard" },
                    { title: "Supplier Response Portal" },
                ]}
                headerName="Supplier Response Portal"
                hideHeader
            />

            <Box sx={{ flexGrow: 1, px: 3, pb: 4 }}>
                {/* ── Page Header ── */}
                <Box sx={{ mb: 3 }}>
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
                        <Box>
                            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
                                Review change requests and provide your response
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* ── Summary Cards ── */}
                {/* <div className="container">
                    <Cards cards={cardData} showBorder={true} callBackFunction={callBackFunction} />
                </div> */}
            

                {/* ── Tabs ── */}
                <Box
                    sx={{
                        display: "flex",
                        borderBottom: "2px solid #E5E7EB",
                        mb: 2.5,
                        gap: 0,
                    }}
                >
                    {[
                        { key: "all", label: "All Requests" },
                        { key: "pending", label: "Pending Response" },
                        { key: "responded", label: "Responded" },
                    ].map((tab) => {
                        const active = activeTab === tab.key;
                        return (
                            <Box
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                sx={{
                                    px: 2.5,
                                    py: 1.25,
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: active ? 700 : 500,
                                    color: active ? "#111827" : "#6B7280",
                                    borderBottom: active ? "2px solid #111827" : "2px solid transparent",
                                    mb: "-2px",
                                    transition: "all 0.15s",
                                    userSelect: "none",
                                    "&:hover": { color: "#111827" },
                                }}
                            >
                                {tab.label}
                                {((active && supplierPortalDataSelector?.loading) || tabCounts[tab.key] !== null) && (
                                    <Box
                                        component="span"
                                        sx={{
                                            ml: 1,
                                            px: 0.75,
                                            py: 0.15,
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            borderRadius: "8px",
                                            backgroundColor: active ? "#111827" : "#E5E7EB",
                                            color: active ? "#fff" : "#6B7280",
                                        }}
                                    >
                                        {active && supplierPortalDataSelector?.loading ? "..." : tabCounts[tab.key]}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                 <Box display="flex" flexDirection="row" justifyContent="space-between" gap={2} sx={{ mt: 2, mb: 1 }}>
                                    {/* Filter Buttons */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton onClick={handleClickHide}>
                                <Badge badgeContent={selectedColumns.length} sx={{
                                    "& .MuiBadge-badge": {
                                        fontSize: "0.6rem", // Smaller font size
                                        height: "16px", // Adjust badge height
                                        minWidth: "16px", // Adjust badge width
                                        padding: "4px", // Adjust padding
                                        background: '#000',
                                        color: '#fff'
                                    },
                                }}>
                                    <MoreVertIcon />
                                </Badge>
                            </IconButton>

                            <Popover
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={handleCloseHide}
                                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                                className="hidePopover"
                                sx={{
                                    marginTop: 1,
                                    marginLeft: 0,
                                }}
                            >
                                <Box sx={{ padding: '10px', width: 150 }}>
                                    <p style={{ marginBottom: 10, marginTop: 5 }}>Hide Columns</p>
                                    <Select
                                        multiple
                                        value={selectedColumns}
                                        onChange={handleColumnChange}
                                        renderValue={renderDropdownValue}
                                        displayEmpty
                                        sx={{ backgroundColor: "#fff", border: '1px solid #000', width: '100%', height: 35, marginBottom: 5 }}
                                    >
                                        <MenuItem disabled value="">
                                            <em>Select Columns</em>
                                        </MenuItem>
                                        {columnFilterDropdownOption?.map(col => (
                                            <MenuItem key={col.value} value={col.value}>
                                                <Checkbox checked={selectedColumns?.includes(col.value)} />
                                                <ListItemText primary={col.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <CustomButton type="secondary" label="Save" onClick={handleSave} />
                                </Box>
                            </Popover>
                        </Box>

                        <CustomButton
                            maxWidth="150px"
                            label="Clear All Filters"
                            bgColor="black"
                            color="white"
                            disabled={disableFilterBtn}
                            onClick={clearAllFilters}
                        />
                    </Box>
                </Box>

                {/* ── Request Table ── */}
                <div className="ag-theme-quartz" style={{ height: 400, width: "100%" }}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={filtered}
                        columnDefs={columnDefs}
                        gridOptions={gridOptions}
                        onGridReady={onGridReady}
                    />
                </div>
            </Box>

            {/* ── Response Dialog ── */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                        Respond to Change Request
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
                        PO {selectedReq?.PO_NO} / Item {selectedReq?.ITEM} — {selectedReq?.MATERIAL_ID}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Change Summary */}
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, color: "#374151" }}>
                        Requested Changes
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr 1fr 1fr",
                            gap: 1.5,
                            mb: 2.5,
                        }}
                    >
                        {selectedReq && [
                            fieldRow("Delivery Date", selectedReq.DELIVERY_DATE, selectedReq.NEW_DELIVERY_DATE),
                            fieldRow("Handover Date", selectedReq.HANDOVER_DATE, selectedReq.NEW_HANDOVER_DATE),
                            fieldRow("Quantity", `${selectedReq.PO_QUANTITY} ${selectedReq.UOM}`, `${selectedReq.NEW_PO_QUANTITY} ${selectedReq.UOM}`),
                            fieldRow("Transport Mode", selectedReq.PO_SHIPPING_INSTRUCTION, selectedReq.NEW_PO_SHIPPING_INSTRUCTION),
                        ].map((f) => (
                            <Paper
                                key={f.label}
                                elevation={0}
                                sx={{
                                    p: 1.5,
                                    borderRadius: "8px",
                                    border: `1px solid ${f.changed ? "#FCA5A5" : "#E5E7EB"}`,
                                    backgroundColor: f.changed ? "#FFF5F5" : "#FAFAFA",
                                }}
                            >
                                <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block", mb: 0.25 }}>
                                    {f.label}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "#9CA3AF", textDecoration: "line-through", display: "block" }}
                                >
                                    {f.oldVal}
                                </Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: f.changed ? "#DC2626" : "#111827" }}>
                                    {f.newVal}
                                </Typography>
                            </Paper>
                        ))}
                    </Box>

                    {/* Buyer Note */}
                    {selectedReq?.buyerNote && (
                        <Box
                            sx={{
                                mb: 2.5,
                                p: 1.5,
                                backgroundColor: "#EFF6FF",
                                borderRadius: "8px",
                                border: "1px solid #BFDBFE",
                            }}
                        >
                            <Typography variant="caption" fontWeight={600} sx={{ color: "#1E3A5F", display: "block", mb: 0.25 }}>
                                Buyer Note
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#1E3A5F" }}>
                                {selectedReq.buyerNote}
                            </Typography>
                        </Box>
                    )}

                    <Divider sx={{ mb: 2.5 }} />

                    {/* Response Type */}
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: "#374151" }}>
                        Your Response
                    </Typography>
                    <Box display="flex" gap={1.5} sx={{ mb: 2 }}>
                        {[
                            { type: "APPROVED", label: "Accept", icon: <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />, activeColor: "#166534", activeBg: "#DCFCE7", activeBorder: "#86EFAC" },
                            { type: "REJECTED", label: "Decline", icon: <CancelOutlinedIcon sx={{ fontSize: 18 }} />, activeColor: "#991B1B", activeBg: "#FEE2E2", activeBorder: "#FCA5A5" },
                            { type: "COUNTER", label: "Counter Offer", icon: <SwapHorizIcon sx={{ fontSize: 18 }} />, activeColor: "#581C87", activeBg: "#F3E8FF", activeBorder: "#D8B4FE" },
                        ].map((opt) => {
                            const active = responseType === opt.type;
                            return (
                                <Box
                                    key={opt.type}
                                    onClick={() => setResponseType(opt.type)}
                                    sx={{
                                        flex: 1,
                                        p: 1.5,
                                        borderRadius: "8px",
                                        border: `2px solid ${active ? opt.activeBorder : "#E5E7EB"}`,
                                        backgroundColor: active ? opt.activeBg : "#fff",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 0.5,
                                        transition: "all 0.15s",
                                        "&:hover": { borderColor: opt.activeBorder },
                                        color: active ? opt.activeColor : "#6B7280",
                                    }}
                                >
                                    {opt.icon}
                                    <Typography variant="caption" fontWeight={active ? 700 : 500} sx={{ fontSize: "12px" }}>
                                        {opt.label}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Counter Offer fields */}
                    {responseType === "COUNTER" && (
                        <>
                        <Box display="flex" gap={1.5} sx={{ mb: 2 }}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Proposed Delivery Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={counterDate}
                                onChange={(e) => setCounterDate(e.target.value)}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                label="Proposed Handover Date"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={counterHandoverDate}
                                onChange={(e) => setCounterHandoverDate(e.target.value)}
                            />
                            {selectedReq?.isQuantityChanged && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Proposed Quantity"
                                    type="number"
                                    value={counterQty}
                                    onChange={(e) => setCounterQty(e.target.value)}
                                    InputProps={{ inputProps: { min: 0 } }}
                                />
                            )}
                        </Box>
                        {selectedReq?.isShippingChanged && (
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    select
                                    label="Proposed Shipping Instruction"
                                    value={counterShippingInstruction}
                                    onChange={(e) => setCounterShippingInstruction(e.target.value)}
                                >
                                    {[
                                        "00 | Road LTL",
                                        "01 | Road FTL",
                                        "02 | Pickup",
                                        "03 | Air",
                                        "04 | Service",
                                        "05 | Rail",
                                        "06 | Sea",
                                        "V1 | Shipping instruction 1",
                                        "V2 | Shipping instruction 2"
                                    ].map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}
                        </>
                    )}

                    {/* Notes */}
                    <TextField
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes / Comments (optional)"
                        placeholder="Add any additional context or remarks..."
                        value={responseNote}
                        onChange={(e) => setResponseNote(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setDialogOpen(false)}
                        sx={{
                            textTransform: "none",
                            borderColor: "#E5E7EB",
                            color: "#374151",
                            "&:hover": { borderColor: "#9CA3AF", backgroundColor: "#F9FAFB" },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitResponse}
                        sx={{
                            backgroundColor: "#1E3A5F",
                            "&:hover": { backgroundColor: "#162d4a" },
                            textTransform: "none",
                            color: "#fff",
                        }}
                    >
                        Submit Response
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Detail Dialog ── */}
            <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>Request Details</Typography>
                    <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.25 }}>
                        PO {detailReq?.poNo} / Item {detailReq?.poItem}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {detailReq && (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                            }}
                        >
                            {[
                                { label: "Material", value: detailReq.material },
                                { label: "Material ID", value: detailReq.materialId },
                                { label: "Plant", value: detailReq.plant },
                                { label: "Incoterm", value: detailReq.incoterm },
                                { label: "MRP Controller", value: detailReq.mrpController },
                                { label: "Original Delivery", value: detailReq.originalDelivery },
                                { label: "Requested Delivery", value: detailReq.requestedDelivery },
                                { label: "Qty", value: `${detailReq.requestedQty} ${detailReq.uom}` },
                                { label: "Transport (Original)", value: detailReq.originalTransport },
                                { label: "Transport (Requested)", value: detailReq.requestedTransport },
                            ].map((f) => (
                                <Box key={f.label}>
                                    <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block" }}>
                                        {f.label}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={500} sx={{ color: "#111827" }}>
                                        {f.value}
                                    </Typography>
                                </Box>
                            ))}
                            {detailReq.buyerNote && (
                                <Box sx={{ gridColumn: "1 / -1" }}>
                                    <Typography variant="caption" sx={{ color: "#9CA3AF", display: "block" }}>
                                        Buyer Note
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#374151" }}>
                                        {detailReq.buyerNote}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => setDetailOpen(false)}
                        sx={{
                            backgroundColor: "#111827",
                            "&:hover": { backgroundColor: "#1f2937" },
                            textTransform: "none",
                            color: "#fff",
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default POExceptionMgmtSupplier;